import dayjs from 'dayjs';
import { useQuery } from 'react-query';
import styled from 'styled-components';
// eslint-disable-next-line
import { zhCN } from 'date-fns/locale';
import { useNavigate } from 'react-router-dom';
import { CloseOutlined } from '@ant-design/icons';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import 'react-big-calendar/lib/css/react-big-calendar.css';
// eslint-disable-next-line
import { parse, getDay, format, startOfWeek } from 'date-fns';
import { useRef, useState, useEffect, useLayoutEffect } from 'react';
import { dateFnsLocalizer, Calendar as BigCalendar } from 'react-big-calendar';
import {
  Tag,
  Card,
  Badge,
  Layout,
  Button,
  Select,
  Divider,
  Breadcrumb,
  Typography,
} from 'design-system';

import { useConfigStore } from 'src/stores/config';
import { queryKeys } from 'src/constants/queryKeys';
import type { ActionPlan } from 'src/services/actionPlan';
import { getMyActionPlans } from 'src/services/actionPlan';

import ListPlanPreview from './ListPlanPreview';
import ActionPlanDetail from '../action-plan-detail';
import ActionPlanEditor from '../action-plan-editor';

const { Content } = Layout;
const { Text } = Typography;

// 加载dayjs插件
dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

// 获取进度状态信息
const getProgressStatus = (
  record: ActionPlan
): { text: string; color: 'default' | 'error' | 'success' | 'warning' | 'processing' } => {
  const { status, assessmentDimension, actualEndDate, planEndDate } = record;

  // 当 assessmentDimension != 'Quantity' 且 status == 'NotStarted' 时，显示"未完成"
  if (assessmentDimension !== 'Quantity' && status === 'NotStarted') {
    return { text: '未完成', color: 'error' };
  }

  // 如果状态是已完成
  if (status === 'Completed') {
    // 如果考核维度是Duration，需要比较时间
    if (assessmentDimension === 'Duration' && actualEndDate && planEndDate) {
      const actualEnd = dayjs(actualEndDate);
      const planEnd = dayjs(planEndDate);

      if (actualEnd.isBefore(planEnd)) {
        return { text: '提前完成', color: 'success' };
      }
      if (actualEnd.isSame(planEnd, 'day')) {
        return { text: '按时完成', color: 'processing' };
      }
      return { text: '延后完成', color: 'warning' };
    }
    // 其他维度或没有时间信息时，显示已完成
    return { text: '已完成', color: 'success' };
  }

  // 默认状态（NotStarted但assessmentDimension为Quantity的情况）
  return { text: '--', color: 'default' };
};

// 来源映射
const sourceMap: Record<string, string> = {
  BusinessPlan: '目标',
  Project: '项目策略',
  ManualCreated: '手动创建',
  VarianceAnalysis: '差异分析',
};

// 日历单元格中的计划项样式
const PlanItem = styled.div<{
  isStart: boolean;
  isEnd: boolean;
  isOnly: boolean;
  backgroundColor: string;
}>`
  margin: 2px 0;
  padding: 2px 8px;
  border-radius: ${({ isStart, isEnd, isOnly }) => {
    if (isOnly) return '4px';
    if (isStart) return '4px 0 0 4px';
    if (isEnd) return '0 4px 4px 0';
    return '0';
  }};
  background-color: ${({ backgroundColor }) => backgroundColor};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-size: 12px;
  height: 24px;
  line-height: 20px;
  display: flex;
  align-items: center;
  cursor: pointer;
  border: 1px solid ${({ backgroundColor }) => adjustColor(backgroundColor, -15)};
  transition: all 0.2s;

  &:hover {
    filter: brightness(1.05);
    transform: translateY(-1px);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }
`;

// 辅助函数：调整颜色亮度
function adjustColor(color: string, amount: number): string {
  // 如果是十六进制颜色
  if (color.startsWith('#')) {
    let r = parseInt(color.slice(1, 3), 16);
    let g = parseInt(color.slice(3, 5), 16);
    let b = parseInt(color.slice(5, 7), 16);

    r = Math.max(0, Math.min(255, r + amount));
    g = Math.max(0, Math.min(255, g + amount));
    b = Math.max(0, Math.min(255, b + amount));

    return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
  }

  // 默认返回原色
  return color;
}

// 新增：获取当前月所有周的日期数组
function getWeeksOfMonth(year: number, month: number) {
  const firstDay = dayjs()
    .year(year)
    .month(month - 1)
    .startOf('month')
    .startOf('week');
  const lastDay = dayjs()
    .year(year)
    .month(month - 1)
    .endOf('month')
    .endOf('week');
  const weeks: dayjs.Dayjs[][] = [];
  let cursor = firstDay.clone();
  while (cursor.isBefore(lastDay) || cursor.isSame(lastDay, 'day')) {
    const week: dayjs.Dayjs[] = [];
    for (let i = 0; i < 7; i++) {
      week.push(cursor.clone());
      cursor = cursor.add(1, 'day');
    }
    weeks.push(week);
  }
  return weeks;
}

const locales = { 'zh-CN': zhCN };
const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek: () => startOfWeek(new Date(), { weekStartsOn: 1 }),
  getDay,
  locales,
});

// 定义自定义事件类型，包含id
interface MyCalendarEvent {
  id: string;
  title: string;
  start: Date;
  end: Date;
  allDay: boolean;
  resource: ActionPlan;
}

export default function ActionPlanCalendarView() {
  const navigate = useNavigate();
  const { calendarConfig } = useConfigStore();
  const [selectedDate, setSelectedDate] = useState<dayjs.Dayjs>(dayjs());
  const [selectedDepartment, setSelectedDepartment] = useState<string | 'all'>('all');
  const [selectedYear, setSelectedYear] = useState<number>(dayjs().year());
  const [selectedMonth, setSelectedMonth] = useState<number>(dayjs().month() + 1);
  const [openDetail, setOpenDetail] = useState(false);
  const [openEditor, setOpenEditor] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<ActionPlan | null>(null);
  const [editorAction, setEditorAction] = useState<'create' | 'edit'>('create');
  const [yearOptions, setYearOptions] = useState<number[]>([]);
  const [currentMonthPlans, setCurrentMonthPlans] = useState<ActionPlan[]>([]);
  const [openMorePlansModal, setOpenMorePlansModal] = useState(false);
  const [currentDatePlans, setCurrentDatePlans] = useState<ActionPlan[]>([]);
  const [currentDateStr, setCurrentDateStr] = useState<string>('');
  // 新增预览卡片的状态
  const [showPlanPreview, setShowPlanPreview] = useState(false);
  const [previewPosition, setPreviewPosition] = useState<{ x: number; y: number }>({
    x: window.innerWidth / 2,
    y: window.innerHeight / 2,
  });
  // 新增计划列表状态
  const [showPlansList, setShowPlansList] = useState(false);
  const [plansListPosition, setPlansListPosition] = useState({ x: 0, y: 0 });
  const [hoveredPlanId, setHoveredPlanId] = useState<string | null>(null);
  // 选中事件的id
  const [selectedEventId, setSelectedEventId] = useState<string | null>(null);
  const [mousePosition, setMousePosition] = useState({
    x: window.innerWidth / 2,
    y: window.innerHeight / 2,
  });
  // 新增：列表hover详情弹窗状态
  const [listPreview, setListPreview] = useState<{
    plan: ActionPlan | null;
    x: number;
    y: number;
    visible: boolean;
  }>({ plan: null, x: 0, y: 0, visible: false });
  const [previewHover, setPreviewHover] = useState(false);
  const hoverTimer = useRef<NodeJS.Timeout | null>(null);
  // 计划详情弹窗ref和位置自适应
  const previewRef = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    if (showPlanPreview && previewRef.current) {
      const rect = previewRef.current.getBoundingClientRect();
      let y = previewPosition.y;
      if (y + rect.height > window.innerHeight) {
        y = window.innerHeight - rect.height - 10;
      }
      if (y < 0) y = 10;
      if (y !== previewPosition.y) setPreviewPosition((pos) => ({ ...pos, y }));
    }
    // eslint-disable-next-line
  }, [showPlanPreview, previewRef.current]);

  // 计划列表弹窗ref和位置自适应
  const plansListRef = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    if (showPlansList && plansListRef.current) {
      const rect = plansListRef.current.getBoundingClientRect();
      let y = plansListPosition.y;
      if (y + rect.height > window.innerHeight) {
        y = window.innerHeight - rect.height - 10;
      }
      if (y < 0) y = 10;
      // 只做微调，避免大幅跳动
      if (Math.abs(y - plansListPosition.y) > 2) setPlansListPosition((pos) => ({ ...pos, y }));
    }
    // eslint-disable-next-line
  }, [showPlansList, plansListRef.current]);

  // hover详情弹窗ref和位置自适应
  const listPreviewRef = useRef<HTMLDivElement>(null);
  const [listPreviewY, setListPreviewY] = useState<number | null>(null);
  useLayoutEffect(() => {
    if (showPlansList && (hoveredPlanId || previewHover) && listPreviewRef.current) {
      const rect = listPreviewRef.current.getBoundingClientRect();
      let y = plansListPosition.y - 200;
      if (y + rect.height > window.innerHeight) {
        y = window.innerHeight - rect.height - 10;
      }
      if (y < 0) y = 10;
      setListPreviewY(y);
    }
    // eslint-disable-next-line
  }, [showPlansList, hoveredPlanId, previewHover, plansListPosition.y, listPreviewRef.current]);

  // 获取当月第一天和最后一天
  const getMonthRange = (year: number, month: number) => {
    const startDate = dayjs()
      .year(year)
      .month(month - 1)
      .startOf('month');
    const endDate = dayjs()
      .year(year)
      .month(month - 1)
      .endOf('month');
    return { startDate, endDate };
  };

  // 获取当月的行动计划数据
  const { data: myActionPlans, isLoading } = useQuery({
    queryKey: [queryKeys.getAllActionPlans, selectedYear, selectedMonth],
    queryFn: async () => {
      const { startDate, endDate } = getMonthRange(selectedYear, selectedMonth);
      return getMyActionPlans({
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString(),
      });
    },
  });

  // 当年月改变时重新获取数据
  useEffect(() => {
    if (myActionPlans) {
      setCurrentMonthPlans(myActionPlans);
    }
  }, [myActionPlans, selectedYear, selectedMonth]);

  // 部门列表 (从API数据中提取)
  const departments = Array.from(
    new Set(currentMonthPlans.filter((plan) => plan.department).map((plan) => plan.department?.id))
  ).map((id) => {
    const dept = currentMonthPlans.find((plan) => plan.department?.id === id)?.department;
    return { id, name: dept?.name || `部门${id}` };
  });

  // 生成年份选项
  useEffect(() => {
    if (calendarConfig) {
      // 从配置中获取年份范围，如果没有配置则使用默认值
      const minYear = dayjs().year() - 5;
      const maxYear = dayjs().year() + 5;

      // 根据范围生成年份数组
      const years = [];
      for (let year = minYear; year <= maxYear; year += 1) {
        years.push(year);
      }
      setYearOptions(years);
    } else {
      // 默认显示当前年份前后5年
      const currentYear = dayjs().year();
      setYearOptions([
        currentYear - 5,
        currentYear - 4,
        currentYear - 3,
        currentYear - 2,
        currentYear - 1,
        currentYear,
        currentYear + 1,
        currentYear + 2,
        currentYear + 3,
        currentYear + 4,
        currentYear + 5,
      ]);
    }
  }, [calendarConfig]);

  // 更新年月选择
  const handleYearChange = (year: number) => {
    setSelectedYear(year);
    setSelectedDate((prev) => prev.year(year));
  };

  const handleMonthChange = (month: number) => {
    setSelectedMonth(month);
    setSelectedDate((prev) => prev.month(month - 1));
  };

  // 根据部门过滤计划
  const filteredPlans = currentMonthPlans.filter(
    (plan) =>
      selectedDepartment === 'all' || (plan.department && plan.department.id === selectedDepartment)
  );

  const handleBackToList = () => {
    navigate('/dashboard/action-plan');
  };

  // 计划转为react-big-calendar事件
  const calendarEvents: MyCalendarEvent[] = filteredPlans.map((plan) => {
    const start = dayjs(plan.planStartDate).startOf('day').toDate();
    let end: Date;
    if (!plan.planEndDate || dayjs(plan.planEndDate).isSame(plan.planStartDate, 'day')) {
      // 单天事件
      end = start;
    } else {
      // 区间事件
      end = dayjs(plan.planEndDate).add(1, 'day').startOf('day').toDate();
    }

    return {
      id: plan.id || '',
      title: plan.description || '',
      start,
      end,
      allDay: true,
      resource: plan,
    };
  });

  // 事件点击弹窗（用于日历格子里的日程）
  const handleSelectEvent = (
    event: MyCalendarEvent,
    e?: React.SyntheticEvent | React.MouseEvent
  ) => {
    setSelectedPlan(event.resource);
    let x = window.innerWidth / 2;
    let y = window.innerHeight / 2;
    // 兼容原生和合成事件
    if (e && 'clientX' in e && 'clientY' in e) {
      const mouseEvent = e as React.MouseEvent;
      x = mouseEvent.clientX;
      y = mouseEvent.clientY;
    }
    setPreviewPosition({ x, y });
    setShowPlanPreview(true);
    setSelectedEventId(event.id);
  };

  // 渲染月视图的单元格内容
  const monthCellRender = (date: dayjs.Dayjs) => {
    const plansForMonth = filteredPlans.filter((plan) => {
      const startDate = dayjs(plan.planStartDate);
      const endDate = dayjs(plan.planEndDate);
      const monthStart = date.startOf('month');
      const monthEnd = date.endOf('month');

      return (
        // 开始日期在当月
        ((startDate.isAfter(monthStart) || startDate.isSame(monthStart)) &&
          (startDate.isBefore(monthEnd) || startDate.isSame(monthEnd))) ||
        // 结束日期在当月
        ((endDate.isAfter(monthStart) || endDate.isSame(monthStart)) &&
          (endDate.isBefore(monthEnd) || endDate.isSame(monthEnd))) ||
        // 跨越整个月
        (startDate.isBefore(monthStart) && endDate.isAfter(monthEnd))
      );
    });

    if (plansForMonth.length === 0) return null;

    return (
      <div>
        <div style={{ fontSize: '14px', marginBottom: '8px' }}>
          <span>{`共 ${plansForMonth.length} 个计划`}</span>
        </div>
        <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
          {plansForMonth.slice(0, 2).map((plan) => {
            const status = getProgressStatus(plan);
            return (
              <li key={plan.id} style={{ marginBottom: '4px' }}>
                <Badge status={status.color} text={renderPlanDescription(plan.description)} />
              </li>
            );
          })}
        </ul>
      </div>
    );
  };

  // 在组件内部添加这个辅助函数
  const renderPlanDescription = (description?: string): string => {
    if (!description) return '-';
    if (description.length > 15) return `${description.substring(0, 15)}...`;
    return description;
  };

  // 为Calendar组件添加自定义类
  const calendarClass = 'action-plan-calendar';

  // 关闭计划列表窗口
  const handleClosePlansList = () => {
    setShowPlansList(false);
  };

  // 处理计划项点击
  const handlePlanItemClick = (plan: ActionPlan, event: React.MouseEvent) => {
    // 不再关闭计划列表窗口
    // setShowPlansList(false);

    // 显示预览窗口
    handleOpenPlanPreview(plan, event);
  };

  // 渲染计划列表窗口
  const renderPlansList = () => {
    if (!showPlansList) return null;
    // 直接隐藏前2个items
    const hiddenPlans = currentDatePlans.slice(2);
    return (
      <div
        id="plans-list-popup"
        ref={plansListRef}
        style={{
          position: 'fixed',
          top: `${plansListPosition.y}px`,
          left: `${plansListPosition.x}px`,
          zIndex: 1001,
          transform: 'translate(-50%, -100%)',
          marginTop: '-10px',
          backgroundColor: 'white',
          borderRadius: '8px',
          boxShadow: '0 3px 12px rgba(0,0,0,0.15)',
          width: '400px',
          maxHeight: '500px',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* 标题栏 */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '16px 20px',
          }}
        >
          <Typography.Title level={5} style={{ margin: 0 }}>
            {currentDateStr}
          </Typography.Title>
          <Button
            type="text"
            icon={<CloseOutlined />}
            onClick={handleClosePlansList}
            aria-label="关闭"
          />
        </div>

        <Divider style={{ margin: 0 }} />

        {/* 计划列表 */}
        <div
          style={{
            padding: '8px 0',
            overflow: 'auto',
            maxHeight: '400px',
          }}
        >
          {hiddenPlans.map((plan) => (
            <div
              key={plan.id}
              style={{
                padding: '12px 20px',
                cursor: 'pointer',
                backgroundColor: hoveredPlanId === plan.id ? '#135fc8' : 'transparent',
                transition: 'background-color 0.2s',
                display: 'flex',
                alignItems: 'center',
                color: hoveredPlanId === plan.id ? 'white' : 'inherit',
              }}
              onMouseEnter={() => {
                if (hoverTimer.current) clearTimeout(hoverTimer.current);
                setHoveredPlanId(plan.id || null);
              }}
              onMouseLeave={() => {
                hoverTimer.current = setTimeout(() => setHoveredPlanId(null), 100);
              }}
              role="button"
              tabIndex={0}
            >
              <div
                style={{
                  width: '8px',
                  height: '8px',
                  borderRadius: '50%',
                  backgroundColor: hoveredPlanId === plan.id ? 'white' : '#1677ff',
                  marginRight: '12px',
                  flexShrink: 0,
                }}
              />
              <div style={{ overflow: 'hidden' }}>
                <div
                  style={{
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    fontWeight: 500,
                  }}
                >
                  {plan.code} {plan.description}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  // 渲染计划预览卡片
  const renderPlanPreview = () => {
    if (!selectedPlan || !showPlanPreview) return null;
    const status = getProgressStatus(selectedPlan);
    // 让详情弹窗只在打开时定位
    return (
      <div
        ref={previewRef}
        style={{
          position: 'fixed',
          top: previewPosition.y,
          left: previewPosition.x,
          zIndex: 9999,
          transform: 'translate(-10px, 10px)', // 稍微偏移避免遮挡鼠标
          marginTop: 0,
        }}
      >
        <Card
          style={{
            width: 360,
            boxShadow: '0 3px 6px rgba(0,0,0,0.15)',
            borderRadius: '8px',
          }}
          bodyStyle={{ padding: 0 }}
        >
          <div style={{ padding: '16px 20px' }}>
            {/* 卡片标题和状态 */}
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '16px',
              }}
            >
              <Typography.Title level={5} style={{ margin: 0 }}>
                {selectedPlan.code || '行动计划'}
              </Typography.Title>
              <Tag color={status.color}>{status.text}</Tag>
            </div>

            {/* 计划信息内容 */}
            <div>
              <div style={{ display: 'flex', marginBottom: '12px' }}>
                <div
                  style={{
                    width: 90,
                    color: '#666',
                    flexShrink: 0,
                    textAlign: 'right',
                    paddingRight: 8,
                  }}
                >
                  考核部门：
                </div>
                <div style={{ flex: 1 }}>{selectedPlan.department?.name || '-'}</div>
              </div>
              <div style={{ display: 'flex', marginBottom: '12px' }}>
                <div
                  style={{
                    width: 90,
                    color: '#666',
                    flexShrink: 0,
                    textAlign: 'right',
                    paddingRight: 8,
                  }}
                >
                  责任人：
                </div>
                <div style={{ flex: 1 }}>
                  {selectedPlan.coordinators
                    ?.map((c) => `${c.lastName}${c.firstName}`)
                    .join('、') || '-'}
                </div>
              </div>
              <div style={{ display: 'flex', marginBottom: '12px' }}>
                <div
                  style={{
                    width: 90,
                    color: '#666',
                    flexShrink: 0,
                    textAlign: 'right',
                    paddingRight: 8,
                  }}
                >
                  行动计划描述：
                </div>
                <div style={{ flex: 1, wordBreak: 'break-all', whiteSpace: 'pre-line' }}>
                  {selectedPlan.description || '-'}
                </div>
              </div>
              <div style={{ display: 'flex', marginBottom: '12px' }}>
                <div
                  style={{
                    width: 90,
                    color: '#666',
                    flexShrink: 0,
                    textAlign: 'right',
                    paddingRight: 8,
                  }}
                >
                  来源：
                </div>
                <div style={{ flex: 1 }}>
                  {sourceMap[selectedPlan.source as string] || selectedPlan.source || '-'}
                </div>
              </div>
              <div style={{ display: 'flex', marginBottom: '4px' }}>
                <div
                  style={{
                    width: 90,
                    color: '#666',
                    flexShrink: 0,
                    textAlign: 'right',
                    paddingRight: 8,
                  }}
                >
                  计划任务周期：
                </div>
                <div style={{ flex: 1 }}>
                  {selectedPlan.planStartDate && selectedPlan.planEndDate
                    ? `${dayjs(selectedPlan.planStartDate).format('YYYY-MM-DD')} 至 ${dayjs(selectedPlan.planEndDate).format('YYYY-MM-DD')}`
                    : '-'}
                </div>
              </div>
            </div>
          </div>

          <Divider style={{ margin: '0' }} />

          {/* 按钮操作区 */}
          <div
            style={{
              display: 'flex',
              justifyContent: 'flex-end',
              padding: '12px 20px',
              gap: '12px',
            }}
          >
            <Button
              type="default"
              style={{
                border: '1px solid #105FC8',
                color: '#105FC8',
                background: '#fff',
                display: 'flex',
                alignItems: 'center',
                fontWeight: 500,
                borderRadius: 8,
                height: 36,
                padding: '0 20px',
              }}
              onClick={handleOpenEditor}
            >
              <img
                src="/assets/icons/common/c-edit.svg"
                alt="编辑"
                style={{ width: 16, height: 16, marginRight: 8 }}
              />
              编辑
            </Button>
            <Button
              type="default"
              style={{
                border: '1px solid #105FC8',
                color: '#105FC8',
                background: '#fff',
                display: 'flex',
                alignItems: 'center',
                fontWeight: 500,
                borderRadius: 8,
                height: 36,
                padding: '0 20px',
              }}
              onClick={handleOpenDetail}
            >
              <img
                src="/assets/icons/common/c-detail.svg"
                alt="详情"
                style={{ width: 16, height: 16, marginRight: 8 }}
              />
              详情
            </Button>
          </div>
        </Card>
      </div>
    );
  };

  // 修改为显示预览卡片
  const handleOpenPlanPreview = (plan: ActionPlan, event: React.MouseEvent) => {
    event.stopPropagation();
    setSelectedPlan(plan);
    // 弹窗高度360px
    let y = event.clientY;
    const popupHeight = 360;
    if (y + popupHeight > window.innerHeight) {
      y -= popupHeight;
      if (y < 0) y = 10; // 最小边界
    }
    setPreviewPosition({ x: event.clientX, y });
    setShowPlanPreview(true);
  };

  // 关闭预览卡片
  const handleClosePlanPreview = () => {
    setShowPlanPreview(false);
    setSelectedPlan(null);
    setSelectedEventId(null);
  };

  // 打开详情抽屉
  const handleOpenDetail = () => {
    setShowPlanPreview(false);
    setOpenDetail(true);
  };

  const handleCloseDetail = () => {
    setOpenDetail(false);
    setSelectedPlan(null);
  };

  const handleOpenEditor = () => {
    setShowPlanPreview(false);
    setEditorAction('edit');
    setOpenEditor(true);
  };

  const handleCloseEditor = () => {
    setOpenEditor(false);
    setSelectedPlan(null);
  };

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      setMousePosition({ x: e.clientX, y: e.clientY });
    };
    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  const hoveredPlan = currentDatePlans.find((p) => p.id === hoveredPlanId);

  // useEffect监听全局点击
  useEffect(() => {
    if (!showPlansList) return;
    const handleClick = (e: MouseEvent) => {
      const plansListEl = document.getElementById('plans-list-popup');
      const previewEl = document.getElementById('plans-list-preview');
      if (plansListEl && plansListEl.contains(e.target as Node)) return;
      if (previewEl && previewEl.contains(e.target as Node)) return;
      setShowPlansList(false);
      setHoveredPlanId(null);
      setPreviewHover(false);
    };
    document.addEventListener('mousedown', handleClick, true);
    return () => document.removeEventListener('mousedown', handleClick, true);
  }, [showPlansList]);

  return (
    <Content style={{ minHeight: '100vh' }}>
      {/* 头部 */}
      <div style={{ marginBottom: 24 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Breadcrumb
            items={[
              {
                title: '行动计划管理',
                href: '/dashboard/action-plan',
              },
              {
                title: '日程表',
              },
            ]}
          />
          <div>
            <Button onClick={handleBackToList} style={{ marginRight: 8 }}>
              返回
            </Button>
          </div>
        </div>
      </div>

      {/* 自定义CSS样式 */}
      <style>
        {`
          /* 防止所有可能的滚动条容器显示滚动条 */
          .${calendarClass} .ant-picker-cell,
          .${calendarClass} .ant-picker-calendar-date,
          .${calendarClass} .ant-picker-calendar-date-content,
          .${calendarClass} .ant-picker-calendar-date-content > div {
            overflow: hidden !important;
          }
          
          /* 隐藏所有标准日历边框，避免分割线问题 */
          .${calendarClass} .ant-picker-cell,
          .${calendarClass} .ant-picker-body,
          .${calendarClass} .ant-picker-content,
          .${calendarClass} .ant-picker-cell-in-view,
          .${calendarClass} .ant-picker-calendar-date {
            border: none !important;
            box-shadow: none !important;
          }
          
          /* 防止溢出导致滚动条 */
          .${calendarClass} .ant-picker-cell *,
          .${calendarClass} .ant-picker-content * {
            overflow: visible !important;
          }
          
          /* 确保日历单元格高度一致 */
          .${calendarClass} .ant-picker-calendar-date-content {
            height: auto !important;
            min-height: 125px !important; /* 确保所有单元格高度一致 */
            position: relative;
          }

          /* 添加轻细的下边框和右边框来代替日历单元格的边界 */
          .${calendarClass} .ant-picker-calendar-date {
            box-shadow: 1px 1px 0 0 rgba(240, 240, 240, 0.5) !important;
            padding: 0 !important;
            margin: 0 !important;
            pointer-events: none; /* 禁止日期单元格的交互事件 */
          }
          
          /* 确保日期内的内容（计划项）仍然可以点击 */
          .${calendarClass} .ant-picker-calendar-date-content {
            pointer-events: auto;
          }

          .${calendarClass} .ant-picker-cell {
            padding: 0 !important;
          }
          /* 禁用日历单元格的悬停效果 */
          .${calendarClass} .ant-picker-cell:hover .ant-picker-calendar-date,
          .${calendarClass} .ant-picker-cell-in-view:hover .ant-picker-calendar-date {
            background: transparent !important;
          }
          .${calendarClass} .ant-picker-cell-in-view.ant-picker-cell-today .ant-picker-calendar-date-today::before {
            border: 1px solid #135fc8;
          }
          .${calendarClass} .plan-container {
            position: relative;
            width: 100%;
            height: 36px;
            margin-bottom: 2px; /* 减少计划项之间的间距，从4px减少到2px */
            display: flex;
            align-items: center;
            cursor: pointer;
            transition: all 0.2s ease;
            background-color: #e7f1ff !important;
            border: none !important;
            box-sizing: border-box !important;
          }
          /* 单日计划项不使用背景色 */
          .${calendarClass} .plan-single {
            background-color: transparent !important;
            border: none !important;
            padding-left: 8px !important;
            z-index: 5 !important;
          }
          
          /* 使用JS添加的高亮类来处理悬停效果 */
          .${calendarClass} .plan-hover {
            opacity: 1 !important;
            background-color: #135fc8 !important;
            box-shadow: 0 0 4px rgba(19, 95, 200, 0.3) inset !important;
            z-index: 10 !important;
          }
          
          /* 当计划项被hover时，内部的蓝点变为白色 */
          .${calendarClass} .plan-hover .plan-text div {
            background-color: white !important;
          }
          
          /* 处理相邻连接元素的样式 - 默认状态 */
          .${calendarClass} [data-has-next="true"],
          .${calendarClass} .plan-container[data-has-next="true"] {
            margin-right: -1px !important;
            border-right: none !important;
            border-top-right-radius: 0 !important;
            border-bottom-right-radius: 0 !important;
            width: calc(100% + 1px) !important;
          }
          
          .${calendarClass} [data-has-prev="true"],
          .${calendarClass} .plan-container[data-has-prev="true"] {
            margin-left: -1px !important;
            border-left: none !important;
            border-top-left-radius: 0 !important;
            border-bottom-left-radius: 0 !important;
            width: calc(100% + 1px) !important;
          }
          
          /* 特殊情况：中间的计划项需要更多处理 */
          .${calendarClass} .plan-middle,
          .${calendarClass} .plan-container.plan-middle {
            margin-left: -1px !important;
            margin-right: -1px !important;
            width: calc(100% + 2px) !important;
            border-radius: 0 !important;
          }
          
          .${calendarClass} .plan-hover .plan-text {
            color: white !important;
          }
          
          /* 基础类样式 */
          .${calendarClass} .plan-start {
            border-top-left-radius: 4px !important;
            border-bottom-left-radius: 4px !important;
            padding-left: 8px !important;
            z-index: 5 !important;
          }
          
          .${calendarClass} .plan-middle {
            padding-left: 4px !important;
            z-index: 1 !important;
          }
          
          .${calendarClass} .plan-end {
            border-top-right-radius: 4px !important;
            border-bottom-right-radius: 4px !important;
            padding-left: 4px !important;
            z-index: 1 !important;
          }
          
          .${calendarClass} .plan-text {
            color: #333;
            font-size: 12px;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
            width: 100%;
          }
          
          .${calendarClass} .ant-picker-calendar-header {
            display: none;
          }
          .${calendarClass} .ant-picker-panel {
            border: 1px solid #f0f0f0;
            border-radius: 8px;
          }
          
          /* 单日计划项的hover效果 */
          .${calendarClass} .plan-single.plan-hover {
            background-color: #135fc8 !important;
          }

          /* 区间日程（多天）浅蓝色 */
          .rbc-event.range-event {
            background: #E6F1FF !important;
            color: #105FC8 !important;
            border: none !important;
            box-shadow: none !important;
            font-weight: 500;
          }
          /* 选中时深蓝色 */
          .rbc-event.selected-event {
            background: #105FC8 !important;
            color: #fff !important;
            border: none !important;
            box-shadow: 0 2px 8px rgba(16,95,200,0.08) !important;
            font-weight: 600;
          }
          /* 单天日程透明 */
          .rbc-event.single-event {
            background: transparent !important;
            color: #105FC8 !important;
            border: none !important;
            box-shadow: none !important;
            font-weight: 500;
          }
        `}
      </style>

      {/* 日历视图 */}
      <div
        style={{
          padding: 16,
          backgroundColor: '#fff',
          borderRadius: '8px',
          boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)',
        }}
      >
        {/* 自定义日历头部 */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 16,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <span style={{ marginRight: 8, fontSize: 14, fontWeight: 600, color: '#525866' }}>
              我的日程表：
            </span>
            <Select
              value={selectedYear}
              onChange={handleYearChange}
              style={{ width: 100, marginRight: 8 }}
              options={yearOptions.map((year) => ({ value: year, label: `${year}年` }))}
            />
            <Select
              value={selectedMonth}
              onChange={handleMonthChange}
              style={{ width: 80 }}
              options={Array.from({ length: 12 }, (_, i) => i + 1).map((month) => ({
                value: month,
                label: `${month}月`,
              }))}
            />
          </div>
        </div>

        <BigCalendar
          localizer={localizer}
          events={calendarEvents}
          startAccessor="start"
          endAccessor="end"
          date={selectedDate.toDate()}
          style={{ height: 700 }}
          views={['month']}
          selectable={false}
          onSelectEvent={handleSelectEvent}
          onShowMore={(
            events: MyCalendarEvent[],
            date: Date,
            e?: React.MouseEvent<HTMLDivElement, MouseEvent>
          ) => {
            if (e && typeof e.preventDefault === 'function') e.preventDefault();
            setCurrentDatePlans(events.map((ev) => ev.resource));
            setCurrentDateStr(dayjs(date as unknown as Date).format('YYYY-MM-DD dddd'));
            setTimeout(() => {
              // 计划列表弹窗预估高度500px，提前修正Y，避免闪烁
              let y = mousePosition.y;
              const estimatedHeight = 500;
              if (y + estimatedHeight > window.innerHeight) {
                y = window.innerHeight - estimatedHeight - 10;
              }
              if (y < 0) y = 10;
              setPlansListPosition({ x: mousePosition.x, y });
              setShowPlansList(true);
            }, 0);
          }}
          messages={{
            month: '月',
            week: '周',
            day: '日',
            today: '今天',
            previous: '上个月',
            next: '下个月',
            showMore: (total, date) => (
              <span
                role="button"
                tabIndex={0}
                onClick={(e) => {
                  setCurrentDateStr(dayjs(date as unknown as Date).format('YYYY-MM-DD dddd'));
                  setPlansListPosition({ x: e.clientX, y: e.clientY });
                  setShowPlansList(true);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    // 键盘事件没有clientX/clientY，兜底用plansList弹窗居中
                    setCurrentDateStr(dayjs(date as unknown as Date).format('YYYY-MM-DD dddd'));
                    setPlansListPosition({ x: window.innerWidth / 2, y: window.innerHeight / 2 });
                    setShowPlansList(true);
                  }
                }}
                style={{ color: '#105FC8', cursor: 'pointer', userSelect: 'none' }}
              >
                {`还有${total}个日程`}
              </span>
            ),
          }}
          formats={{
            weekdayFormat: (date: Date) => {
              const days = ['周日', '周一', '周二', '周三', '周四', '周五', '周六'];
              return days[date.getDay()];
            },
          }}
          eventPropGetter={(event) => {
            const startDate = dayjs(event.start);
            const endDate = dayjs(event.end);
            // 单天事件：start和end同一天
            const isSingleDay = startDate.isSame(endDate, 'day');
            // 区间事件：start和end不同天
            const isRange = !isSingleDay;
            let className = '';
            if (isSingleDay) className = 'single-event';
            else if (isRange) className = 'range-event';
            if (selectedEventId === event.id) className += ' selected-event';
            return { className };
          }}
          components={{
            event: ({ event }) => {
              // 判断是否为单天（start和end相差<=1天且日期相同）
              const startDate = new Date(event.start);
              const endDate = new Date(event.end);
              const isSingleDay =
                startDate.toDateString() === endDate.toDateString() ||
                (endDate.getTime() - startDate.getTime() <= 24 * 60 * 60 * 1000 &&
                  dayjs(startDate).isSame(endDate, 'day'));
              return (
                <div>{isSingleDay ? event.resource?.description || event.title : event.title}</div>
              );
            },
          }}
        />
      </div>

      {/* 详情抽屉 */}
      <ActionPlanDetail open={openDetail} onClose={handleCloseDetail} data={selectedPlan} />

      {/* 编辑抽屉 */}
      <ActionPlanEditor
        open={openEditor}
        onClose={handleCloseEditor}
        action={editorAction}
        data={selectedPlan}
      />

      {/* 计划列表窗口 */}
      {renderPlansList()}

      {/* 计划预览卡片 */}
      {renderPlanPreview()}

      {/* 添加一个点击外部关闭预览卡片的遮罩 */}
      {showPlanPreview && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 999,
          }}
          onClick={() => setShowPlanPreview(false)}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Escape' || e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              setShowPlanPreview(false);
            }
          }}
        />
      )}

      {/* 在plansList弹窗后渲染ListPlanPreview */}
      {showPlansList && (hoveredPlanId || previewHover) && hoveredPlan && (
        <ListPlanPreview
          id="plans-list-preview"
          plan={hoveredPlan}
          x={
            plansListPosition.x > window.innerWidth / 2
              ? plansListPosition.x - 560
              : plansListPosition.x + 200
          }
          y={listPreviewY !== null ? listPreviewY : plansListPosition.y - 200}
          onEdit={() => {
            setShowPlanPreview(false);
            setHoveredPlanId(null);
            setSelectedPlan(hoveredPlan);
            setEditorAction('edit');
            setOpenEditor(true);
          }}
          onDetail={() => {
            setShowPlanPreview(false);
            setHoveredPlanId(null);
            setSelectedPlan(hoveredPlan);
            setOpenDetail(true);
          }}
          onMouseEnter={() => {
            if (hoverTimer.current) clearTimeout(hoverTimer.current);
            setPreviewHover(true);
          }}
          onMouseLeave={() => {
            setPreviewHover(false);
            hoverTimer.current = setTimeout(() => setHoveredPlanId(null), 100);
          }}
          onCloseList={() => {
            setShowPlansList(false);
            setHoveredPlanId(null);
            setPreviewHover(false);
          }}
        />
      )}
    </Content>
  );
}
